import { useCallback, useEffect, useState } from "react"

import styles from "./EditMappingModal.module.scss"

import { getFragmentData } from "@/company_admin/gql/__generated__"
import { graphql } from "@/company_admin/gql/__generated__"
import { GetCompanySalons } from "@/company_admin/pages/company_admin/CompanyAdmin"
import ModalDialog from "@/main/components/ui/ModalDialog/ModalDialog"
import {
	useCompanyAdminClient,
	useCompanyAdminMutation,
	useCompanyAdminQuery,
} from "@/provider/apollo/Apollo"

const editMappingSalonFragment = graphql(/* GraphQL */ `
	fragment EditMappingSalonFields on AccountingGroupSalon {
		salonId
		salonName
	}
`)

const EditMappingGetAccountingGroupDetail = graphql(/* GraphQL */ `
	query EditMappingGetAccountingGroupDetail(
		$param: GetAccountingGroupDetailParam!
	) {
		getAccountingGroupDetail(param: $param) {
			salons {
				...EditMappingSalonFields
			}
		}
	}
`)

const EditCompanyAllAccountingGroups = graphql(/* GraphQL */ `
	mutation EditCompanyAllAccountingGroups(
		$input: EditCompanyAllAccountingGroupsInput!
	) {
		editCompanyAllAccountingGroups(input: $input) {
			accountingGroups {
				id
			}
		}
	}
`)

type SalonAssignment = {
	salonId: number
	salonName: string
	accountingGroupId: string | null
}

type Props = {
	accountingGroups: { id: string; name: string }[]
	refetchGroups: () => void
	refetchDetail: () => void
	refetchSettlements: () => void
}

export const useEditMappingModal = ({
	accountingGroups,
	refetchGroups,
	refetchDetail,
	refetchSettlements,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	const [assignments, setAssignments] = useState<SalonAssignment[]>([])
	const [validationError, setValidationError] = useState<string | null>(null)
	const client = useCompanyAdminClient()

	const { data: companySalonsData } = useCompanyAdminQuery(GetCompanySalons, {
		variables: { param: {} },
		skip: !isOpen,
	})

	// モーダルが開いた時に、各グループの詳細を命令的に取得して紐付け状態を構築
	useEffect(() => {
		if (!isOpen) return
		if (!companySalonsData?.getCompanySalons?.salons) return

		const allSalons = companySalonsData.getCompanySalons.salons

		const fetchGroupDetails = async () => {
			const salonGroupMap = new Map<number, string>()

			const results = await Promise.all(
				accountingGroups.map(
					async (group) =>
						await client.query({
							query: EditMappingGetAccountingGroupDetail,
							variables: {
								param: { accountingGroupId: group.id },
							},
							fetchPolicy: "network-only",
						})
				)
			)

			for (let i = 0; i < results.length; i++) {
				const result = results[i]
				const group = accountingGroups[i]
				if (!result.data?.getAccountingGroupDetail?.salons) continue
				const salons = getFragmentData(
					editMappingSalonFragment,
					result.data.getAccountingGroupDetail.salons
				)
				for (const salon of salons) {
					salonGroupMap.set(salon.salonId, group.id)
				}
			}

			setAssignments(
				allSalons.map((salon) => ({
					salonId: salon.salonId,
					salonName: salon.salonName,
					accountingGroupId: salonGroupMap.get(salon.salonId) ?? null,
				}))
			)
		}

		fetchGroupDetails()
	}, [isOpen, companySalonsData, accountingGroups, client])

	const [editAllGroups, { loading }] = useCompanyAdminMutation(
		EditCompanyAllAccountingGroups
	)

	const handleAssignmentChange = useCallback(
		(salonId: number, groupId: string | null) => {
			setAssignments((prev) =>
				prev.map((a) =>
					a.salonId === salonId ? { ...a, accountingGroupId: groupId } : a
				)
			)
		},
		[]
	)

	const handleAssignAllToGroup = useCallback((groupId: string | null) => {
		setAssignments((prev) =>
			prev.map((a) => ({ ...a, accountingGroupId: groupId }))
		)
	}, [])

	const handleSave = useCallback(async () => {
		setValidationError(null)

		const groupedAssignments = new Map<string, number[]>()

		for (const assignment of assignments) {
			if (assignment.accountingGroupId) {
				const existing =
					groupedAssignments.get(assignment.accountingGroupId) ?? []
				existing.push(assignment.salonId)
				groupedAssignments.set(assignment.accountingGroupId, existing)
			}
		}

		await editAllGroups({
			variables: {
				input: {
					assignments: Array.from(groupedAssignments.entries()).map(
						([accountingGroupId, salonIds]) => ({
							accountingGroupId,
							salonIds,
						})
					),
				},
			},
		})

		refetchGroups()
		refetchDetail()
		refetchSettlements()
		setIsOpen(false)
	}, [
		assignments,
		editAllGroups,
		refetchGroups,
		refetchDetail,
		refetchSettlements,
	])

	const render = useCallback(() => {
		return (
			<ModalDialog
				isOpen={isOpen}
				onOpenChange={setIsOpen}
				contentClassName={styles.modal_wide}
			>
				<div className={styles.modal_content}>
					<h2 className={styles.modal_title}>経理グループ紐付け編集</h2>
					<div className={styles.matrix_container}>
						<table className={styles.matrix_table}>
							<thead>
								<tr>
									<th className={styles.salon_header}>店舗名</th>
									{accountingGroups.map((group) => (
										<th key={group.id} className={styles.group_header}>
											<label className={styles.group_header_label}>
												<input
													type="radio"
													name="assign-all-group"
													className={styles.group_header_radio}
													checked={
														assignments.length > 0 &&
														assignments.every(
															(a) => a.accountingGroupId === group.id
														)
													}
													onChange={() => handleAssignAllToGroup(group.id)}
												/>
												{group.name}
											</label>
										</th>
									))}
									<th className={styles.group_header}>
										<label className={styles.group_header_label}>
											<input
												type="radio"
												name="assign-all-group"
												className={styles.group_header_radio}
												checked={
													assignments.length > 0 &&
													assignments.every((a) => a.accountingGroupId === null)
												}
												onChange={() => handleAssignAllToGroup(null)}
											/>
											未割当
										</label>
									</th>
								</tr>
							</thead>
							<tbody>
								{assignments.map((assignment) => (
									<tr key={assignment.salonId}>
										<td className={styles.salon_cell}>
											{assignment.salonName}
										</td>
										{accountingGroups.map((group) => (
											<td key={group.id} className={styles.radio_cell}>
												<input
													type="radio"
													name={`salon-${assignment.salonId}`}
													checked={assignment.accountingGroupId === group.id}
													onChange={() =>
														handleAssignmentChange(assignment.salonId, group.id)
													}
												/>
											</td>
										))}
										<td className={styles.radio_cell}>
											<input
												type="radio"
												name={`salon-${assignment.salonId}`}
												checked={assignment.accountingGroupId === null}
												onChange={() =>
													handleAssignmentChange(assignment.salonId, null)
												}
											/>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					{validationError && (
						<p className={styles.validation_error}>{validationError}</p>
					)}
					<div className={styles.buttons}>
						<button
							type="button"
							className={styles.cancel_button}
							onClick={() => setIsOpen(false)}
							disabled={loading}
						>
							キャンセル
						</button>
						<button
							type="button"
							className={styles.submit_button}
							onClick={handleSave}
							disabled={loading}
						>
							{loading ? "保存中..." : "保存"}
						</button>
					</div>
				</div>
			</ModalDialog>
		)
	}, [
		isOpen,
		assignments,
		accountingGroups,
		handleAssignmentChange,
		handleAssignAllToGroup,
		handleSave,
		loading,
		validationError,
	])

	return {
		render,
		openModal: () => setIsOpen(true),
	}
}
